/**
 * Validación y normalización de RUT/RUN chileno (módulo 11).
 *
 * Espejo exacto de app/Support/Rut.php: si tocas el algoritmo aquí, tócalo allí.
 * Sin dependencias externas: son veinte líneas de aritmética.
 *
 * FORMATO CANÓNICO, único, para persistir y mostrar: cuerpo-DV sin puntos y con el
 * dígito verificador en MAYÚSCULA -> "20347878-K".
 *
 * ⚠️ REGLA DE CAJA — es la parte que más se rompe:
 *   - HACIA ADENTRO (aritmética): clean() y computeDv() devuelven MINÚSCULA ('k'),
 *     para que isValid() compare sin ambigüedad un valor escrito de cualquier forma.
 *   - HACIA AFUERA (input, API, presentación): format(), maskInput() y resolveDv()
 *     emiten MAYÚSCULA.
 *
 *   NO "arregles" clean() ni computeDv() para que devuelvan 'K': isValid() compara
 *   computeDv() contra el DV que limpió clean(), y cambiar solo una de las dos deja
 *   inválido TODO RUT terminado en K, sin dar ningún error.
 */

/** Ejemplo de formato, para los mensajes de error. */
export const RUT_EXAMPLE = '20347878-K';

/**
 * Deja solo lo significativo, en minúscula: fuera puntos, guion, espacios y adornos.
 */
export function clean(value) {
  return String(value == null ? '' : value).toLowerCase().replace(/[^0-9k]/g, '');
}

/**
 * Dígito verificador por módulo 11: se recorre el cuerpo de DERECHA a IZQUIERDA con
 * el multiplicador ciclando 2,3,4,5,6,7. Devuelve 'k' minúscula a propósito.
 */
export function computeDv(body) {
  let sum = 0;
  let multiplier = 2;

  for (let i = String(body).length - 1; i >= 0; i--) {
    sum += parseInt(String(body).charAt(i), 10) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }

  const rest = 11 - (sum % 11);

  if (rest === 11) { return '0'; }
  if (rest === 10) { return 'k'; }
  return String(rest);
}

/**
 * Separa cuerpo y DV. El DV es SIEMPRE el último carácter: no se deduce por longitud,
 * porque los cuerpos van de 1 a 8 dígitos.
 */
export function split(value) {
  const c = clean(value);

  if (c.length < 2) {
    return { body: c, dv: '' };
  }

  return { body: c.slice(0, -1), dv: c.slice(-1) };
}

/** Formato canónico. Hacia afuera, el DV en MAYÚSCULA. */
export function format(value) {
  const { body, dv } = split(value);

  if (!body || !dv) {
    return clean(value).toUpperCase();
  }

  return body + '-' + dv.toUpperCase();
}

/** ¿Es un RUT válido? Acepta cualquier caja y cualquier adorno. */
export function isValid(value) {
  const c = clean(value);

  if (c.length < 2) { return false; }

  const { body, dv } = split(c);

  // El cuerpo tiene que ser 100 % numérico: sin esto, una 'k' en medio
  // ("12k45678-5") sobrevive a clean() y llega a la aritmética.
  if (!/^[0-9]+$/.test(body)) { return false; }

  return computeDv(body) === dv;
}

/**
 * Máscara en vivo, pensada para un watcher del campo. Es PERMISIVA a propósito: el
 * usuario escribe de izquierda a derecha y no se puede adivinar dónde termina el
 * cuerpo salvo que sea inequívoco. NO la "mejores" cortando por longitud fija.
 *
 * 1. Si el usuario escribió un guion, se RESPETA: lo anterior es cuerpo (solo
 *    dígitos) y lo posterior el DV. Es lo que permite cuerpos de 7 dígitos
 *    ("1234567-4") sin parches como rellenar con ceros por la izquierda.
 * 2. Sin guion, el guion se agrega SOLO cuando ya no hay ambigüedad: el último
 *    carácter es 'k' (que solo puede ser DV), o hay 9 o más caracteres (cuerpo de
 *    8 + DV).
 * 3. Con 8 caracteres o menos y sin guion, NO se toca nada: puede ser un cuerpo de 7
 *    al que le falta el DV, o uno de 8 todavía a medio escribir.
 */
export function maskInput(value) {
  const raw = String(value == null ? '' : value);

  if (raw === '') { return ''; }

  const dashAt = raw.indexOf('-');

  if (dashAt !== -1) {
    // Caso 1: el usuario ya decidió dónde corta.
    const body = raw.slice(0, dashAt).replace(/[^0-9]/g, '');
    const dv = raw.slice(dashAt + 1).toLowerCase().replace(/[^0-9k]/g, '').slice(0, 1);
    return body + '-' + dv.toUpperCase();
  }

  // Sin guion: se limpia y se quitan las 'k' internas, que siempre son un error de
  // tipeo; solo se conserva la 'k' final, que sí puede ser el DV.
  const c = clean(raw);
  const endsWithK = c.slice(-1) === 'k';
  const digits = c.replace(/k/g, '');
  const normalized = digits + (endsWithK ? 'k' : '');

  if (endsWithK) {
    // Caso 2a: una 'k' solo puede ser DV, así que el corte es inequívoco.
    return digits + '-K';
  }

  if (normalized.length >= 9) {
    // Caso 2b: cuerpo de 8 + DV.
    return normalized.slice(0, -1) + '-' + normalized.slice(-1).toUpperCase();
  }

  // Caso 3: todavía es ambiguo, no se toca.
  return normalized;
}

/**
 * ¿Está completo para guardar? EXIGE el guion explícito.
 *
 * "232214" no se interpreta como "23221-4" ni se le calcula el DV por su cuenta: se
 * marca como formato inválido para que lo escriba bien. Si no, se guardaría en
 * silencio un RUT que el usuario nunca escribió.
 */
export function isComplete(value) {
  const raw = String(value == null ? '' : value);

  if (raw.indexOf('-') === -1) { return false; }

  return isValid(raw);
}

/**
 * Botón "DV", doble función:
 *   - si el valor YA trae guion, solo VALIDA lo ingresado;
 *   - si no, trata todo como cuerpo, CALCULA el DV, lo agrega y valida.
 *
 * Devuelve `computed` para que la interfaz pueda distinguir "lo calculé yo" de
 * "lo validé", que son dos mensajes distintos para el usuario.
 */
export function resolveDv(value) {
  const raw = String(value == null ? '' : value);

  if (raw.indexOf('-') !== -1) {
    return { value: format(raw), valid: isValid(raw), computed: false };
  }

  const body = clean(raw).replace(/k/g, '');

  if (body === '') {
    return { value: raw, valid: false, computed: false };
  }

  const full = body + '-' + computeDv(body).toUpperCase();

  return { value: full, valid: isValid(full), computed: true };
}

/**
 * Documentos que NO son RUT (pasaporte, DNI extranjero...): trim + MAYÚSCULA y fuera
 * puntos, comas y espacios, para que todo el catálogo comparta estándar de caja.
 */
export function normalizeDocumentCase(value) {
  return String(value == null ? '' : value).trim().toUpperCase().replace(/[.,\s]/g, '');
}

/**
 * ¿Aplica la validación de RUT? Solo si el país configurado es Chile Y el tipo de
 * documento elegido lo exige. Fuera de ese contexto no debe interferir.
 *
 * Se compara por ABREVIATURA y no por id: los ids son datos de la tabla
 * `documenttypes` y el catálogo crece. La comparación ignora caja y puntuación, así
 * que 'C.I.' y 'CI' son el mismo tipo.
 */
export const RUT_COUNTRY = 'CL';
export const RUT_TYPE_ABBREVIATIONS = ['RUT', 'CI'];

function normalizeAbbreviation(value) {
  return String(value == null ? '' : value).toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * @param countryCode  país configurado en el sistema; llega del backend en la respuesta
 *                     de getDocumenttypes, NO se cablea aquí.
 * @param abbreviation abreviatura del tipo de documento elegido.
 * @param rutCountry   país cuyo documento se valida con módulo 11. Por defecto 'CL',
 *                     pero es parámetro para poder alinearlo con
 *                     config('documents.rut_country') si algún día cambia: si el
 *                     backend validara otro país y el front siguiera comparando contra
 *                     'CL', la máscara y el aviso quedarían desalineados con lo que el
 *                     servidor exige.
 */
export function isRutContext(countryCode, abbreviation, rutCountry) {
  const target = normalizeAbbreviation(rutCountry || RUT_COUNTRY);

  if (normalizeAbbreviation(countryCode) !== target) { return false; }

  const needle = normalizeAbbreviation(abbreviation);

  return RUT_TYPE_ABBREVIATIONS.some(function (candidate) {
    return normalizeAbbreviation(candidate) === needle;
  });
}

/**
 * Regla de formulario. Recibe un GETTER o un booleano de "¿está habilitada?", porque
 * el contexto (país + tipo de documento) es reactivo y se evalúa en cada validación.
 *
 * Devuelve true si no aplica el contexto, y también si el campo está vacío: el vacío
 * lo cubre la regla de "requerido", no esta.
 */
export function rutRule(enabled, message) {
  const text = message || ('El N.º de Documento no es un RUT válido. Ejemplo: ' + RUT_EXAMPLE);

  return function (value) {
    const on = typeof enabled === 'function' ? enabled() : enabled;

    if (!on) { return true; }
    if (value === '' || value === null || value === undefined) { return true; }

    return isComplete(value) || text;
  };
}

/**
 * Segunda barrera, justo antes de enviar. Hace falta además de la regla del campo
 * porque el valor puede haberse fijado por código (rehidratación al editar, búsqueda
 * por documento) sin pasar nunca por el watcher.
 */
export function isValidForSubmit(value, enabled) {
  const on = typeof enabled === 'function' ? enabled() : enabled;

  if (!on) { return { valid: true, message: '' }; }
  if (value === '' || value === null || value === undefined) { return { valid: true, message: '' }; }

  if (isComplete(value)) { return { valid: true, message: '' }; }

  return {
    valid: false,
    message: 'El N.º de Documento no es un RUT válido. Ejemplo: ' + RUT_EXAMPLE,
  };
}

export default {
  RUT_EXAMPLE,
  clean,
  computeDv,
  split,
  format,
  isValid,
  maskInput,
  isComplete,
  resolveDv,
  normalizeDocumentCase,
  isRutContext,
  rutRule,
  isValidForSubmit,
};
